import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccessoryItem, SubscriptionForHardwareOffering } from '@app/sales/models/accessory.model';
import { PriceFormatPipe, PriceType } from '@app/shared/pipes/price-format.pipe';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { VoiceSubscriptionOffering } from '@app/sales/modules/subscription/models/offerings-response.model';
import { OfferingSelectors } from '@app/sales/states/offering/offering.selectors';
import { IconNames } from '@app/shared/enums/icon-names';
import { PcmProduct } from '@app/sales/modules/subscription/models/pcm-product.model';
import { PaymentMethod } from '@app/sales/modules/accessories/models/accessory-options.model';
import { select } from '@ngxs/store';
import { VasInformationComponent } from '@app/shared/components/vas-information/vas-information.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';
import { RadioButtonListItemComponent } from '@app/shared/components/atoms/radio-button-list/radio-button-list-item/radio-button-list-item.component';
import { RadioButtonListComponent } from '@app/shared/components/atoms/radio-button-list/radio-button-list.component';
import { transformPrice } from '@app/core/price-utils';

@Component({
  selector: 'app-subscription-chooser',
  templateUrl: './subscription-chooser.component.html',
  styleUrl: './subscription-chooser.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RadioButtonListComponent,
    RadioButtonListItemComponent,
    MatFormField,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatIcon,
    IceAlertComponent,
    VasInformationComponent,
    PriceFormatPipe,
  ],
})
export class SubscriptionChooserComponent {
  @Input({ required: true }) form: FormGroup;
  @Input({ required: true }) accessoryItem: AccessoryItem;
  @Input({ required: true }) availableSubscriptions: SubscriptionForHardwareOffering[];
  @Input({ required: true }) selectedSubscription: SubscriptionForHardwareOffering;
  @Output() subscriptionChange = new EventEmitter();

  allVoiceOfferings = select(OfferingSelectors.getAllOfferings);

  PaymentMethod = PaymentMethod;
  PriceType = PriceType;
  IconNames = IconNames;

  getVasProductFromOffering = OfferingHelperService.getVasProductFromOffering;

  bindingDiscount(subscription: SubscriptionForHardwareOffering): string {
    const offering = this.getVoiceSubscriptionOffering(subscription);
    const vasProduct = this.getVasProductFromOffering(offering);
    if (vasProduct) {
      const price = vasProduct.monthlyPrice?.price.includingVAT;
      return `${vasProduct.productName} ${price > 0 ? '+' : ''}${transformPrice(price, PriceType.OneTime)}`;
    }

    return `få ${transformPrice(
      this.accessoryItem.priceInformation.price.includingVAT -
        subscription.hardwareOfferingPriceWithSubscription.amountIncludingVAT,
      PriceType.OneTime
    )} i bindingsrabatt`;
  }

  getVoiceSubscriptionOffering(option: SubscriptionForHardwareOffering): VoiceSubscriptionOffering {
    return this.allVoiceOfferings().find(offering => offering.id === option?.subscriptionOfferingId);
  }

  getVasProduct(): PcmProduct {
    return this.getVasProductFromOffering(this.getVoiceSubscriptionOffering(this.selectedSubscription));
  }

  onSubscriptionChange() {
    this.subscriptionChange.emit();
  }

  get calculatedDiscountPrice(): number {
    return (
      this.accessoryItem.priceInformation.price.includingVAT -
      this.selectedSubscription.hardwareOfferingPriceWithSubscription.amountIncludingVAT
    );
  }

  get minimumTotalPrice(): number {
    if (!this.selectedSubscription) {
      return 0;
    }
    const monthlyPrice = this.selectedSubscription.subscriptionMonthlyPrice.amountIncludingVAT;
    const payNow = this.selectedSubscription.hardwareOfferingPriceWithSubscription.amountIncludingVAT;
    const bindingPeriodInMonths = this.selectedSubscription.bindingInfo.bindingPeriodInMonths;

    return payNow + monthlyPrice * bindingPeriodInMonths;
  }
}
